import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { ChangeEvent, Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { Textarea } from "./Textarea";
import { type TextareaDataProps, textareaDataSchema } from "./Textarea.schema";
import { TEXTAREA_TAG_NAME } from "./constants";

export interface TextareaHandle {
  disabled: (value?: boolean) => void;
  setError: (message?: string | null) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [TEXTAREA_TAG_NAME]: { props: TextareaDataProps; handle: TextareaHandle };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-textarea": HTMLElement & TextareaHandle;
  }
}

export function RWCTextarea({
  ref,
  disabled: initialDisabled,
  value: initialValue,
  inputId,
  ...props
}: TextareaDataProps & { ref?: Ref<TextareaHandle> }) {
  const host = useIslandHost();

  const [disabled, setDisabled] = useState(initialDisabled ?? false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const currentValue = useRef(initialValue ?? "");
  const committedValue = useRef(initialValue ?? "");

  const handle: TextareaHandle = {
    disabled: (value = true) => setDisabled(value),
    setError: (message = null) => setErrorMessage(message),
  };

  useImperativeHandle(ref, () => handle, []);

  const syncFormValue = useCallback(() => {
    getElementInternals(host)?.setFormValue(
      disabled ? null : currentValue.current,
    );
  }, [host, disabled]);

  useEffect(syncFormValue, [syncFormValue]);

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    currentValue.current = event.target.value;
    syncFormValue();
  }

  // On blur, once per edit: the native change never leaves the shadow root
  function commit() {
    if (currentValue.current === committedValue.current) {
      return;
    }

    committedValue.current = currentValue.current;
    host?.setAttribute("value", currentValue.current);
    host?.dispatchEvent(
      new CustomEvent("textarea:change", {
        detail: { value: currentValue.current },
        bubbles: true,
      }),
    );
  }

  return (
    <Textarea
      {...props}
      id={inputId}
      defaultValue={initialValue}
      disabled={disabled}
      invalid={errorMessage != null}
      title={errorMessage ?? undefined}
      onChange={handleChange}
      onBlur={commit}
    />
  );
}

componentManager.register(
  rwc(TEXTAREA_TAG_NAME, {
    component: RWCTextarea,
    dataSchema: textareaDataSchema,
    formAssociated: true,
  }),
);
